import { PaletteOptions } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

/**
 * Brand identity. The orange is kept EXACTLY as the existing brand value so the
 * visual identity is preserved — we only build a proper, accessible scale around
 * it. Orange is the single "loud" colour in the system: it means brand + primary
 * action + selection. Everything else stays calm.
 */
export const BRAND = {
  orange: '#FFA500', // primary.main — unchanged brand orange
  orangeLight: '#FFC04D',
  orangeDark: '#E08A00',
  // Dark, warm near-black used as text on top of the bright orange. White on
  // #FFA500 only reaches ~2:1 contrast (fails WCAG); this warm ink reaches ~8.5:1.
  onOrange: '#2A1B00',
} as const;

/**
 * One palette factory for both modes so light and dark can never drift apart.
 * Neutrals are tinted very slightly toward the brand hue (warm) for subconscious
 * cohesion — never pure #000 / #fff.
 */
export const getPalette = (mode: ThemeMode): PaletteOptions => {
  const isLight = mode === 'light';

  const common = {
    primary: {
      main: BRAND.orange,
      light: BRAND.orangeLight,
      dark: BRAND.orangeDark,
      contrastText: BRAND.onOrange,
    },
    common: { white: '#FFFFFF', black: '#0E0D0B' },
  };

  if (isLight) {
    return {
      ...common,
      mode: 'light',
      secondary: {
        main: '#4A4842',
        light: '#6F6B62',
        dark: '#322F2A',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#2E7D46',
        light: '#4C9E63',
        dark: '#1F5E33',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#B7791F',
        light: '#D69A2E',
        dark: '#8A5A12',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#C62828',
        light: '#E04B43',
        dark: '#9B1C1C',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#2563B8',
        light: '#4D86D9',
        dark: '#1B4A8C',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#F7F6F3', // warm light-gray canvas
        paper: '#FFFFFF', // cards/surfaces sit slightly brighter than the canvas
      },
      text: {
        primary: '#1C1B18', // warm near-black
        secondary: '#6B675E', // readable warm gray (was #B3B3B3 — failed contrast)
        disabled: '#A8A39A',
      },
      divider: '#E8E4DC',
      grey: {
        50: '#F7F6F3',
        100: '#EFEDE7',
        200: '#E3E0D8',
        300: '#D2CEC4',
        400: '#B4AFA3',
        500: '#928D82',
        600: '#6B675E',
        700: '#4E4B44',
        800: '#34322D',
        900: '#1C1B18',
      },
      action: {
        active: '#5A564E',
        hover: 'rgba(28, 27, 24, 0.04)',
        selected: 'rgba(255, 165, 0, 0.10)',
        disabled: 'rgba(28, 27, 24, 0.26)',
        disabledBackground: 'rgba(28, 27, 24, 0.10)',
        focus: 'rgba(255, 165, 0, 0.18)',
      },
    };
  }

  // Dark — warm near-black surfaces, never pure black. Brand orange pops.
  return {
    ...common,
    mode: 'dark',
    secondary: {
      main: '#D6D0C6',
      light: '#E7E2DA',
      dark: '#B4AEA3',
      contrastText: '#211F1B',
    },
    success: {
      main: '#5BBE78',
      light: '#7CCB92',
      dark: '#3F9C5C',
      contrastText: '#10140F',
    },
    warning: {
      main: '#E6A93C',
      light: '#EDBB63',
      dark: '#C58A24',
      contrastText: '#1A1407',
    },
    error: {
      main: '#F2685F',
      light: '#F58982',
      dark: '#D14A42',
      contrastText: '#1A0C0B',
    },
    info: {
      main: '#5B9BE8',
      light: '#7EB2EE',
      dark: '#3D7BC9',
      contrastText: '#0B1320',
    },
    background: {
      default: '#161513', // warm near-black canvas
      paper: '#201E1A', // lifted warm surface
    },
    text: {
      primary: '#F4F1EA', // warm off-white
      secondary: '#A8A299', // readable warm gray
      disabled: '#6F6A61',
    },
    divider: '#332F28',
    grey: {
      50: '#1C1B18',
      100: '#211F1B',
      200: '#2A2823',
      300: '#332F28',
      400: '#4E4A42',
      500: '#6F6A61',
      600: '#928D82',
      700: '#B4AFA3',
      800: '#D2CEC4',
      900: '#F4F1EA',
    },
    action: {
      active: '#C9C3B8',
      hover: 'rgba(244, 241, 234, 0.06)',
      selected: 'rgba(255, 165, 0, 0.16)',
      disabled: 'rgba(244, 241, 234, 0.30)',
      disabledBackground: 'rgba(244, 241, 234, 0.12)',
      focus: 'rgba(255, 165, 0, 0.24)',
    },
  };
};

/**
 * CSS custom properties mirrored from the palette. These are injected on :root by
 * CssBaseline for the ACTIVE theme, so legacy SCSS (which hardcoded colours) can
 * consume theme-aware values and finally respond to dark mode.
 */
export const getCssVars = (mode: ThemeMode): Record<string, string> => {
  const isLight = mode === 'light';
  return {
    '--app-bg-default': isLight ? '#F7F6F3' : '#161513',
    '--app-bg-paper': isLight ? '#FFFFFF' : '#201E1A',
    '--app-surface-2': isLight ? '#EFEDE7' : '#2A2823',
    '--app-text-primary': isLight ? '#1C1B18' : '#F4F1EA',
    '--app-text-secondary': isLight ? '#6B675E' : '#A8A299',
    '--app-border': isLight ? '#E8E4DC' : '#332F28',
    '--app-divider': isLight ? '#E8E4DC' : '#332F28',
    '--app-primary': BRAND.orange,
    '--app-primary-contrast': BRAND.onOrange,
    '--app-primary-soft': isLight
      ? 'rgba(255, 165, 0, 0.10)'
      : 'rgba(255, 165, 0, 0.16)',
    '--app-success': isLight ? '#2E7D46' : '#5BBE78',
    '--app-danger': isLight ? '#C62828' : '#F2685F',
    '--app-warning': isLight ? '#B7791F' : '#E6A93C',
    '--app-info': isLight ? '#2563B8' : '#5B9BE8',
    '--app-sidebar-bg': isLight ? '#1C1B18' : '#201E1A',
    '--app-sidebar-hover': 'rgba(255, 255, 255, 0.08)',
    '--app-shadow-color': isLight ? '28, 27, 24' : '0, 0, 0',
  };
};
